import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkle, SpeakerHigh, Palette, UserCircle, Check, ShieldCheck, Trash, Warning } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [prefs, setPrefs] = useState({ theme: 'light', voice_enabled: false, voice_rate: 1.0 });
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  const deleteMyData = async () => {
    setDeleting(true);
    try {
      const { data } = await axios.delete(`${API}/user/data`);
      setDeleteResult(data);
    } catch (e) {
      setDeleteResult({ error: e.response?.data?.detail || 'Something went wrong. Please try again.' });
    } finally { setDeleting(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user/preferences`);
        setPrefs(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => { if (user?.name) setName(user.name); }, [user]);

  const save = async (newPrefs) => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/preferences`, newPrefs);
      setPrefs(newPrefs);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const updatePref = (key, val) => {
    const updated = { ...prefs, [key]: val };
    save(updated);
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="settings-page">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to="/app" className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all" data-testid="settings-back-button">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Settings</h1>
            <p className="text-sm text-white/40">Customize your Lindey experience</p>
          </div>
          {saved && (
            <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="ml-auto flex items-center gap-1.5 text-xs text-lime-400 font-medium">
              <Check weight="bold" className="w-3.5 h-3.5" /> Saved
            </motion.div>
          )}
        </div>

        <div className="space-y-8">
          {/* Profile */}
          <Section icon={UserCircle} title="Profile">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-white/80 mb-1.5 block">Name</label>
                <div className="flex gap-2">
                  <Input value={name} onChange={e => setName(e.target.value)} className="h-10 rounded-lg border-white/10 focus:border-lime-500 focus:ring-lime-500/20 text-sm" data-testid="settings-name-input" />
                  <button onClick={() => updatePref('name', name)} className="px-4 h-10 rounded-lg bg-lime-400/100 hover:bg-lime-600 text-white text-sm font-medium transition-colors" data-testid="settings-save-name">
                    Save
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-white/80 mb-1 block">Email</label>
                <p className="text-sm text-white/50">{user?.email}</p>
              </div>
            </div>
          </Section>

          {/* Appearance */}
          <Section icon={Palette} title="Appearance">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white/80">Theme</p>
                <p className="text-xs text-white/40 mt-0.5">Choose light or dark mode</p>
              </div>
              <div className="flex gap-2">
                {['light', 'dark'].map(t => (
                  <button key={t} onClick={() => updatePref('theme', t)} data-testid={`theme-${t}`}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${
                      prefs.theme === t ? 'bg-lime-400/15 text-lime-300 border border-lime-400/25' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/25'
                    }`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          {/* Voice */}
          <Section icon={SpeakerHigh} title="Voice">
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white/80">Voice output</p>
                  <p className="text-xs text-white/40 mt-0.5">Read AI responses aloud</p>
                </div>
                <Switch checked={prefs.voice_enabled} onCheckedChange={v => updatePref('voice_enabled', v)} data-testid="voice-enabled-toggle" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-white/80">Speech rate</p>
                  <span className="text-xs text-white/40 font-mono">{(prefs.voice_rate || 1).toFixed(1)}x</span>
                </div>
                <Slider
                  value={[prefs.voice_rate || 1]}
                  onValueChange={v => updatePref('voice_rate', v[0])}
                  min={0.5} max={2} step={0.1}
                  className="w-full"
                  data-testid="voice-rate-slider"
                />
              </div>
            </div>
          </Section>

          {/* Privacy & Data */}
          <Section icon={ShieldCheck} title="Privacy & Data">
            <div className="space-y-4">
              <div className="rounded-lg bg-emerald-400/10 border border-emerald-400/20 p-4">
                <p className="text-sm font-medium text-emerald-300 mb-1">The Linkletter Pledge</p>
                <p className="text-xs text-emerald-300/80 leading-relaxed">Everyday chats are processed on your device and never analyzed by our servers. Whatever is stored for syncing, you can erase below — permanently and immediately.</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white/80">Delete my data</p>
                  <p className="text-xs text-white/40 mt-0.5">Permanently wipe all conversations, files and tasks</p>
                </div>
                <button onClick={() => { setDeleteResult(null); setShowDeleteDialog(true); }} data-testid="delete-my-data-button"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-400/10 text-red-400 border border-red-400/20 hover:bg-red-400/20 transition-all">
                  <Trash className="w-3.5 h-3.5" /> Delete my data
                </button>
              </div>
            </div>
          </Section>
        </div>
      </div>

      {/* Delete My Data confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[420px]" data-testid="delete-data-modal">
          {!deleteResult ? (
            <>
              <DialogHeader>
                <div className="w-10 h-10 rounded-xl bg-red-400/10 border border-red-400/20 flex items-center justify-center mb-2">
                  <Warning weight="fill" className="w-5 h-5 text-red-400" />
                </div>
                <DialogTitle className="text-lg font-semibold" style={{ fontFamily: 'Clash Display, sans-serif' }}>Delete all your data?</DialogTitle>
                <DialogDescription className="text-sm text-white/50 pt-1">
                  This permanently erases every conversation, message, uploaded file and agent task from Lindey's servers. This cannot be undone. Your account stays active.
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowDeleteDialog(false)} data-testid="keep-my-data-button"
                  className="flex-1 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white/80 font-semibold text-sm transition-colors">
                  Keep my data
                </button>
                <button onClick={deleteMyData} disabled={deleting} data-testid="confirm-delete-data-button"
                  className="flex-1 py-2.5 rounded-xl bg-red-400/100 hover:bg-red-600 text-white font-semibold text-sm transition-colors disabled:opacity-50">
                  {deleting ? 'Deleting…' : 'Delete everything'}
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="w-10 h-10 rounded-xl bg-lime-400/10 border border-lime-400/20 flex items-center justify-center mb-2">
                  <Check weight="bold" className="w-5 h-5 text-lime-400" />
                </div>
                <DialogTitle className="text-lg font-semibold" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                  {deleteResult.error ? 'Something went wrong' : 'Your data is gone'}
                </DialogTitle>
                <DialogDescription className="text-sm text-white/50 pt-1" data-testid="delete-data-result">
                  {deleteResult.error || `Deleted ${deleteResult.conversations_deleted} conversations, ${deleteResult.messages_deleted} messages and ${deleteResult.files_deleted} files. Permanently.`}
                </DialogDescription>
              </DialogHeader>
              <button onClick={() => setShowDeleteDialog(false)} data-testid="close-delete-modal"
                className="w-full py-2.5 rounded-xl bg-lime-400/100 hover:bg-lime-600 text-white font-semibold text-sm transition-colors">
                Done
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-lime-400/10 border border-lime-400/20 flex items-center justify-center">
          <Icon weight="duotone" className="w-4 h-4 text-lime-400" />
        </div>
        <h2 className="text-base font-semibold" style={{ fontFamily: 'Clash Display, sans-serif' }}>{title}</h2>
      </div>
      {children}
    </motion.div>
  );
}
